<template>
  <v-card class="ma-0 pa-2 card bg-background" color="background" variant="elevated" :elevation="isOverlay ? 0 : 4" density="compact">
    <v-form class="ma-0 pa-0" @submit.prevent density="compact">
      <v-card-item v-if="!show" class="embedding-summary" @click="toggleShow(true)">
        <template #prepend>
          <div class="mr-3 embedding-summary-avatar">
            <img
              v-if="showStandaloneIcon && iconMode !== 'mono'"
              :src="(icon as ProviderIcon).src"
              :alt="`${providerKey || 'embedding'} icon`"
              class="embedding-summary-avatar-image"
              @error="onIconError"
            >
            <span
              v-else-if="showStandaloneIcon"
              class="embedding-summary-avatar-mask"
              :style="iconMaskStyle"
              role="img"
              :aria-label="`${providerKey || 'embedding'} icon`"
            ></span>
            <span v-else class="embedding-summary-initials" :style="{ backgroundColor: brandColor }">{{ embeddingInitials }}</span>
          </div>
        </template>
        <v-card-title class="text-truncate">{{ name || 'Embedding Model' }}</v-card-title>
        <v-card-subtitle class="text-truncate">
          {{ providerKey || 'No provider' }} · {{ modelName || 'No API model selected' }}
        </v-card-subtitle>
      </v-card-item>
      <v-card-text v-if="!show" class="pt-0 pb-0 embedding-summary-text">
        <div class="d-flex align-center">
          <div class="d-flex flex-wrap align-center flex-grow-1">
            <v-chip size="small" variant="tonal" class="mr-2">
              {{ collectionUsageLabel }}
            </v-chip>
          </div>
          <v-btn v-if="!isOverlay" color="primary" icon="mdi-chevron-down" size="large" variant="plain"
            class="embedding-summary-expand" @click.stop="toggleShow(true)" />
        </div>
      </v-card-text>
      <v-expand-transition>
        <v-list class="ma-0 pa-0 bg-background" v-show="show" density="compact">
          <!-- Provider Selection -->
          <v-list-item class="ma-0 pa-0" density="compact">
            <v-select v-model="providerKey" @update:modelValue="providerChanged" :items="providerKeys" label="Provider"
              class="pt-2" variant="outlined" density="compact"
              :rules="[() => !!providerKey || 'Provider is required']"></v-select>
          </v-list-item>

          <!-- Provider Model Selection -->
          <v-list-item class="ma-0 pa-0" density="compact">
            <v-autocomplete v-model="modelName" @update:modelValue="modelSelected" :items="models" item-title="id"
              item-value="id" label="Provider Models" class="pt-2" variant="outlined" density="compact"
              :disabled="!providerKey" :loading="modelsLoading" :readonly="modelsLoading"
              :rules="[() => !!modelName || 'Provider model is required']">
              <template v-slot:append>
                <Button color="primary" buttonClass="ml-n2" :click="syncModels" :disabled="syncDisabled" icon="mdi-sync"
                  size="x-large" tooltip="Sync Models" />
              </template>
            </v-autocomplete>
          </v-list-item>

          <!-- Model Name -->
          <v-list-item v-if="showAdvanced" class="ma-0 pa-0" density="compact">
            <v-text-field v-model="name" @blur="trackFieldUpdate('name')" label="Model Name" class="pt-2"
              :rules="[() => !!name || 'Model Name is required']" variant="outlined" density="compact" clearable>
              <template v-slot:append-inner>
                <v-tooltip text="Autofill from selected API model" v-if="name == null || name == ''">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="primary" icon="mdi-lightning-bolt-outline" @click="autofillName" />
                  </template>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-list-item>
        </v-list>
      </v-expand-transition>
      <v-card-actions v-if="show" density="compact">
        <Button color="primary" class="mr-4" :disabled="saveDisabled" :click="updateModel"
          icon="mdi-check-bold" size="x-large" tooltip="Save Model" density="compact" />
        <Button color="warning" class="mr-4" :disabled="isSystemModel" :click="initiateDeleteModel"
          icon="mdi-trash-can-outline" size="x-large" tooltip="Delete Model" density="compact" />
        <v-spacer />
        <IconToggle :value="showAdvanced" @value="toggleAdvanced" tooltip="Toggle advanced options" />
        <v-btn v-if="!isOverlay" color="primary" class="mx-auto" icon="mdi-chevron-up"
          @click="toggleShow(false)" density="compact"></v-btn>
      </v-card-actions>
    </v-form>
    <div v-if="waiting" class="card-action-overlay">
      <h2>{{ actionMessage }}</h2>
    </div>
    <ConfirmationDialog v-model="showDeleteConfirmationDialog" title="Confirm Embedding Model Deletion"
      message="Are you sure you want to delete this embedding model configuration?" :itemName="modelNameToDelete"
      itemType="embedding model" :loading="waiting" :warningMessage="additionalWarningMessage"
      @confirm="confirmModelDelete" @cancel="cancelDeleteModel" />
  </v-card>
</template>
<script lang="ts">
import Button from '@/components/sub/Button.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import IconToggle from '@/components/sub/IconToggle.vue'
import { Options, mixins } from 'vue-class-component'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import { mapGetters } from 'vuex'
import { RosettaModel } from '@/types/msq'
import { ProviderKey } from '@/types/providers'
import { EmbeddingModelConfig, SystemEmbeddingModelConfig, UserEmbeddingModelConfig, VectorStoreWithFiles } from '@/types/embeddings'
import { ProviderIcon, ThemeName, getProviderBrandColor, getProviderIcon } from '@/utils/providerIcons'

@Options({
  props: {
    modelId: {
      type: String,
      required: false,
      default: ''
    },
    isOverlay: Boolean,
    startExpanded: Boolean
  },
  components: {
    Button,
    ConfirmationDialog,
    IconToggle
  },
  computed: {
    ...mapGetters(['providerEmbeddingModels', 'providerKeys', 'allProviderModels', 'embeddingModelsById', 'vectorStores', 'currentTheme'])
  },
  emits: ['model-deleted', 'remove-card', 'saved']
})
export default class EmbeddingModelCard extends mixins(AnalyticsMixin) {
  public modelId!: string
  public isOverlay!: boolean
  public startExpanded!: boolean
  public providerEmbeddingModels!: RosettaModel[]
  public providerKeys!: string[]
  public allProviderModels!: Record<string, RosettaModel[]>
  public embeddingModelsById!: Record<string, SystemEmbeddingModelConfig | UserEmbeddingModelConfig>
  public vectorStores!: Record<string, VectorStoreWithFiles>
  public currentTheme!: string
  public modelConfig: SystemEmbeddingModelConfig | UserEmbeddingModelConfig | null = null
  public show = true // Start expanded for new cards
  public showAdvanced = false
  public name = ''
  public modelName = ''
  public providerKey: ProviderKey | null = null
  public modelsLoading = false
  public syncDisabled = false
  public saveDisabled = true
  public isSystemModel = false
  public waiting = false
  public actionMessage = ''
  public showDeleteConfirmationDialog = false
  public modelNameToDelete: string | null = null
  public additionalWarningMessage = ''
  public iconLoadFailed = false

  get models(): RosettaModel[] {
    if (!this.providerKey) return []
    // Filter models to only include embedding models
    const allModels = this.allProviderModels[this.providerKey] || []
    return allModels.filter(model => model.id.toLowerCase().includes('embed'))
  }

  get embeddingInitials(): string {
    const raw = (this.name || this.modelName || 'EM').replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'EM'
  }

  get brandColor(): string {
    return getProviderBrandColor(this.providerKey || '')
  }

  get icon(): ProviderIcon | null {
    return this.providerKey ? getProviderIcon(this.providerKey, this.currentTheme as ThemeName) : null
  }

  get iconMode(): ProviderIcon['mode'] | null {
    return this.icon?.mode || null
  }

  get iconMaskStyle(): Record<string, string> {
    const icon = this.icon
    if (!icon) return {}
    const url = `url("${icon.src}")`
    return {
      '-webkit-mask-image': url,
      'mask-image': url,
      backgroundColor: icon.tint || 'currentColor'
    }
  }

  get showStandaloneIcon(): boolean {
    return !!this.icon && this.iconLoadFailed !== true
  }

  get collectionUsageLabel(): string {
    const count = this.isModelUsedInCollections.collectionNames.length
    return count === 1 ? 'Used by 1 collection' : `Used by ${count} collections`
  }

  get isModelUsedInCollections(): { used: boolean; collectionNames: string[] } {
    if (!this.vectorStores || !this.name) {
      return { used: false, collectionNames: [] };
    }
    
    const usedInCollections: string[] = [];
    
    for (const store of Object.values(this.vectorStores)) {
      // Check if this model is used by the vector store
      if (store.embeddingModel && store.embeddingModel.toLowerCase() === this.name.toLowerCase()) {
        usedInCollections.push(store.name);
      }
    }
    
    return {
      used: usedInCollections.length > 0,
      collectionNames: usedInCollections
    };
  }
  
  created() {
    this.initializeData()
  }

  initializeData() {
    // Look for the model in embeddingModelsById
    this.modelConfig = this.embeddingModelsById[this.modelId] ?? null

    if (this.modelConfig) {
      // Access the model data from the config
      const { name, modelName, providerKey } = this.modelConfig.config as EmbeddingModelConfig
      this.name = name
      this.modelName = modelName || name // Use modelName if available, otherwise use name
      this.providerKey = providerKey
      // Existing cards: collapse by default, expand when in overlay or when caller requests it
      this.show = this.isOverlay || this.startExpanded
      if (name !== modelName) {
        this.showAdvanced = true
      }
    } else {
      // New card
      this.show = true // Expand new cards
    }
  }

  providerChanged(newProviderKey: ProviderKey | null) {
    this.trackFeatureUsage('embedding_model', 'select_provider', { provider: newProviderKey });
    this.providerKey = newProviderKey
    // Reset model selection when provider changes
    this.modelName = ''
    this.checkChanges()
  }

  modelSelected(selectedModelName: string) {
    this.trackFeatureUsage('embedding_model', 'select_model', { model_name: selectedModelName });
    this.modelName = selectedModelName
    // Only auto-fill name if this is a new model and name is empty
    if (this.name.trim() === '' && selectedModelName) {
      this.name = selectedModelName
    }
    this.checkChanges()
  }

  checkChanges() {
    // Basic validation: enable save if required fields are filled
    this.saveDisabled = !this.name || !this.providerKey || !this.modelName
  }

  async syncModels() {
    this.trackButtonClick('sync_embedding_models', 'click', { provider: this.providerKey });
    if (!this.providerKey) {
      this.$store.dispatch('updateSnackbar', { text: 'Please select a provider first.', timeout: 3000 })
      return
    }
    this.modelsLoading = true
    this.syncDisabled = true
    // Fetch models for the currently selected providerKey
    await this.$store.dispatch('fetchProviderModels', { providerConfig: { providerKey: this.providerKey } })
    this.modelsLoading = false
    this.syncDisabled = false
  }

  autofillName() {
    this.trackButtonClick('autofill_embedding_model_name', 'click');
    if (this.modelName) {
      this.name = this.modelName
      this.checkChanges()
    }
  }

  async updateModel() {
    const isCreating = !this.modelConfig;
    this.trackButtonClick(isCreating ? 'create_embedding_model' : 'update_embedding_model', 'save', { model_name: this.name });
    if (!this.saveDisabled) {
      this.waiting = true
      this.actionMessage = 'Saving...'
      
      // Ensure the model name contains "embed" for API recognition
      const modelName = this.modelName.toLowerCase().includes('embed') 
        ? this.modelName 
        : `${this.modelName}-embed`;
      
      // Create model config
      const modelConfig = {
        name: this.name,
        description: this.name, // Use name as description if not provided
        providerKey: this.providerKey,
        model: modelName
      };
      
      // Use the addModel API function
      const success = await this.$store.dispatch('setModelConfig', modelConfig);
      
      if (success) {
        this.$store.dispatch('updateSnackbar', {
          text: `Embedding model "${this.name}" saved successfully.`,
          timeout: 3000
        });
        this.show = false; // Collapse after save
        this.$emit('saved', { name: this.name });
      } else {
        this.$store.dispatch('updateSnackbar', { 
          text: `Failed to save embedding model "${this.name}".`,
          timeout: 3000
        });
      }
      
      this.waiting = false
      this.actionMessage = ''
    }
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }

  initiateDeleteModel() {
    this.trackButtonClick('delete_embedding_model_start', 'click', { model_id: this.modelId });
    if (this.isSystemModel) return;

    // If modelConfig is null, it's a new, unsaved card.
    if (!this.modelConfig) {
      this.$emit('remove-card');
      return;
    }
    
    this.modelNameToDelete = this.name;
    
    // Check if model is used in collections
    const { used, collectionNames } = this.isModelUsedInCollections;
    if (used) {
      const collectionList = collectionNames.join(', ');
      this.additionalWarningMessage = `⚠️ Warning: This embedding model is currently used by the following collection${collectionNames.length > 1 ? 's' : ''}: ${collectionList}. ${collectionNames.length > 1 ? 'These collections' : 'This collection'} will no longer function correctly without this embedding model.`;
    } else {
      this.additionalWarningMessage = '';
    }
    
    this.showDeleteConfirmationDialog = true;
  }

  async confirmModelDelete() {
    this.trackButtonClick('delete_embedding_model_confirm', 'confirm', { model_id: this.modelId });
    if (this.modelNameToDelete && this.modelId) {
      this.waiting = true;
      this.actionMessage = 'Deleting...';
      
      const success = await this.$store.dispatch('deleteModel', this.name);
      
      if (success) {
        this.$store.dispatch('updateSnackbar', { 
          text: `Embedding model "${this.name}" deleted successfully.`,
          timeout: 3000
        });
        // The watcher in the parent component will handle the UI update.
      } else {
        this.$store.dispatch('updateSnackbar', { 
          text: `Failed to delete embedding model "${this.name}".`,
          timeout: 3000
        });
      }
      
      this.waiting = false;
      this.actionMessage = '';
    }
    this.cancelDeleteModel();
  }

  cancelDeleteModel() {
    this.trackButtonClick('delete_embedding_model_cancel', 'cancel', { model_id: this.modelId });
    this.showDeleteConfirmationDialog = false;
    this.modelNameToDelete = null;
    this.additionalWarningMessage = '';
  }

  toggleShow(shouldShow: boolean) {
    this.show = shouldShow;
    this.trackButtonClick('toggle_embedding_model_card', shouldShow ? 'expand' : 'collapse', { model_id: this.modelId });
  }

  toggleAdvanced() {
    this.showAdvanced = !this.showAdvanced
    this.trackButtonClick('toggle_advanced_embedding_model', this.showAdvanced ? 'show' : 'hide', { model_id: this.modelId });
  }

  trackFieldUpdate(fieldName: string) {
    this.trackFormInteraction('embedding_model_form', 'update_field', fieldName);
    this.checkChanges();
  }
}
</script>
<style scoped lang="scss">
.embedding-summary {
  cursor: pointer;
}
.embedding-summary-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-on-surface));
}
.embedding-summary-avatar-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.embedding-summary-avatar-mask {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}
.embedding-summary-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fff;
}
.embedding-summary-text {
  padding-left: 72px;
}
.embedding-summary-expand {
  flex-shrink: 0;
  margin-left: 8px;
}
.card-action-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  border-radius: inherit; // Match card radius
}
.max-85 {
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
